import React, { useMemo } from 'react';
import HBText from '../../../HBText/HBText';
import HBCheckbox from '../../../HBCheckbox/HBCheckbox';
import { KanbanCard } from '../../HBKanban.types';
import { KanbanCardHoverAction } from '../HBKanbanCard';
import { useHBKanbanContext } from '../../HBKanbanContext';
import {
    StyledCheckboxWrapper,
    StyledHeaderContainer,
    StyledPreTitle,
    StyledPrefix,
    StyledSuffix,
    StyledActionsContainer,
    StyledMenuWrapper
} from './HBKanbanCardHeader.styles';

const HBKanbanCardHeader = <TItemData = any,>({
    card
}: {
    card?: KanbanCard<TItemData>;
}) => {
    const {
        onToggleSelect,
        isInSelectableState,
        isCardSelected,
        renderProps,
        removeCard,
        testIdName
    } = useHBKanbanContext<TItemData>();

    const selected = onToggleSelect ? isCardSelected(card) : false;
    const preTitle =
        card && renderProps?.renderCardPreTitle
            ? renderProps.renderCardPreTitle(card)
            : null;
    const headerSuffix =
        card && renderProps?.renderCardHeaderSuffix
            ? renderProps.renderCardHeaderSuffix(card, {
                  onDeleteCard: removeCard
              })
            : null;
    const hasHeaderSuffix = !!headerSuffix;

    const checkbox = useMemo(
        () => (
            <StyledCheckboxWrapper
                onClick={event => {
                    event.stopPropagation();
                }}
            >
                <HBCheckbox
                    size="sm"
                    checked={selected}
                    onChange={() => onToggleSelect?.(card)}
                    testIdName="kanban-card"
                />
            </StyledCheckboxWrapper>
        ),
        [card, selected, onToggleSelect]
    );

    // Only show selection UI if selection is enabled (onToggleSelect exists)
    if (!onToggleSelect) {
        return (
            <>
                {preTitle && (
                    <StyledPreTitle
                        data-testid={
                            testIdName
                                ? `${testIdName}-card-pre-title`
                                : undefined
                        }
                    >
                        {preTitle}
                    </StyledPreTitle>
                )}
                <StyledHeaderContainer>
                    <StyledPrefix>
                        <HBText type="emphasis16" color="text.primary">
                            {card?.title}
                        </HBText>
                    </StyledPrefix>
                    {hasHeaderSuffix && (
                        <StyledSuffix>
                            <KanbanCardHoverAction>
                                {headerSuffix}
                            </KanbanCardHoverAction>
                        </StyledSuffix>
                    )}
                </StyledHeaderContainer>
            </>
        );
    }

    return (
        <>
            {preTitle && (
                <StyledPreTitle
                    data-testid={
                        testIdName ? `${testIdName}-card-pre-title` : undefined
                    }
                >
                    {preTitle}
                </StyledPreTitle>
            )}
            <StyledHeaderContainer>
                <StyledPrefix>
                    <HBText type="emphasis16" color="text.primary">
                        {card?.title}
                    </HBText>
                </StyledPrefix>
                <StyledSuffix>
                    <StyledActionsContainer>
                        {hasHeaderSuffix && !isInSelectableState && (
                            <StyledMenuWrapper>
                                <KanbanCardHoverAction>
                                    {headerSuffix}
                                </KanbanCardHoverAction>
                            </StyledMenuWrapper>
                        )}
                        {isInSelectableState ? (
                            checkbox
                        ) : (
                            <KanbanCardHoverAction>
                                {checkbox}
                            </KanbanCardHoverAction>
                        )}
                    </StyledActionsContainer>
                </StyledSuffix>
            </StyledHeaderContainer>
        </>
    );
};

export default HBKanbanCardHeader;
